import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export default function ICalSettingsModal({ properties, onClose, onSynced }) {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const initial = {};
    properties.forEach(p => { initial[p.id] = p.ical_url || ''; });
    setUrls(initial);
  }, [properties]);

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);

    for (const prop of properties) {
      const url = urls[prop.id]?.trim() || null;
      if (url !== (prop.ical_url || null)) {
        const { error } = await supabase
          .from('properties')
          .update({ ical_url: url })
          .eq('id', prop.id);
        if (error) {
          setMessage({ type: 'error', text: `Error saving ${prop.name}: ${error.message}` });
          setLoading(false);
          return;
        }
      }
    }

    setMessage({ type: 'success', text: 'Settings saved.' });
    setLoading(false);
  };

  const handleTestSync = async (propertyId) => {
    setSyncingId(propertyId);
    setMessage(null);

    // Save the URL first
    const url = urls[propertyId]?.trim() || null;
    if (!url) {
      setMessage({ type: 'error', text: 'Enter an iCal URL first.' });
      setSyncingId(null);
      return;
    }

    await supabase
      .from('properties')
      .update({ ical_url: url })
      .eq('id', propertyId);

    const { data, error } = await supabase.functions.invoke('sync-ical', {
      body: { property_id: propertyId },
    });

    if (error) {
      setMessage({ type: 'error', text: `Sync failed: ${error.message}` });
    } else if (data?.success && data.results?.length > 0) {
      const r = data.results[0];
      if (r.error) {
        setMessage({ type: 'error', text: r.error });
      } else {
        setMessage({
          type: 'success',
          text: `${r.property_name}: ${r.bookings_synced} synced, ${r.bookings_removed} removed`,
        });
        if (onSynced) onSynced();
      }
    } else {
      setMessage({ type: 'error', text: 'No results returned from sync.' });
    }

    setSyncingId(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>iCal Feed Settings</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {message && (
            <div className={message.type === 'error' ? 'error-msg' : 'success-msg'}>
              {message.text}
            </div>
          )}

          {properties.map(prop => (
            <div key={prop.id} className="form-group">
              <label>{prop.name}</label>
              <div className="ical-input-row">
                <input
                  type="url"
                  value={urls[prop.id] || ''}
                  onChange={e => setUrls({ ...urls, [prop.id]: e.target.value })}
                  placeholder="https://example.com/calendar.ics"
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => handleTestSync(prop.id)}
                  disabled={syncingId === prop.id || !urls[prop.id]?.trim()}
                >
                  {syncingId === prop.id ? 'Syncing...' : 'Test Sync'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
